import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useRoomStore } from '../stores/roomStore';
import { webrtcService } from '../services/webrtcService';
import toast from 'react-hot-toast';
import {
  MdCall,
  MdCallEnd,
  MdMic,
  MdMicOff,
  MdVideocam,
  MdVideocamOff,
  MdScreenShare,
  MdStopScreenShare,
  MdChat,
} from 'react-icons/md';

export default function VideoConferencePage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const currentRoom = useRoomStore((state) => state.currentRoom);
  const participants = useRoomStore((state) => state.participants);
  const joinRoom = useRoomStore((state) => state.joinRoom);
  const leaveRoom = useRoomStore((state) => state.leaveRoom);
  const addParticipant = useRoomStore((state) => state.addParticipant);
  const removeParticipant = useRoomStore((state) => state.removeParticipant);
  const updateParticipantMedia = useRoomStore((state) => state.updateParticipantMedia);

  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});

  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeConference();
    return () => cleanup();
  }, []);

  const initializeConference = async () => {
    try {
      setIsLoading(true);

      // Get local stream
      const stream = await webrtcService.getLocalStream();
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Connect to SFU
      await webrtcService.connect(accessToken);

      // Join room
      await joinRoom(accessToken, parseInt(roomId));

      // Setup event listeners
      webrtcService.on('userJoined', (data) => {
        toast.success(`${data.userName} joined`);
        addParticipant(data);
      });

      webrtcService.on('userLeft', (data) => {
        toast.info(`${data.userName || 'User'} left`);
        removeParticipant(data.socketId);
        if (remoteVideoRefs.current[data.socketId]) {
          delete remoteVideoRefs.current[data.socketId];
        }
      });

      webrtcService.on('remoteStream', (data) => {
        if (remoteVideoRefs.current[data.socketId]) {
          remoteVideoRefs.current[data.socketId].srcObject = data.stream;
        }
      });

      webrtcService.on('userMediaChange', (data) => {
        updateParticipantMedia(data.socketId, {
          videoEnabled: data.videoEnabled,
          audioEnabled: data.audioEnabled,
        });
      });

      webrtcService.on('error', (error) => {
        toast.error(error.message || 'Connection error');
      });

      setIsLoading(false);
    } catch (error) {
      console.error('Initialize error:', error);
      toast.error('Failed to start video conference');
      navigate('/rooms');
    }
  };

  const cleanup = async () => {
    try {
      webrtcService.stopLocalStream();
      if (currentRoom) {
        await leaveRoom(accessToken, currentRoom.id);
      }
      webrtcService.disconnect();
    } catch (error) {
      console.error('Cleanup error:', error);
    }
  };

  const handleToggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
    webrtcService.toggleAudio(!isAudioEnabled);
  };

  const handleToggleVideo = () => {
    setIsVideoEnabled(!isVideoEnabled);
    webrtcService.toggleVideo(!isVideoEnabled);
  };

  const handleStartScreenShare = async () => {
    try {
      if (isScreenSharing) {
        webrtcService.stopScreenShare();
        setIsScreenSharing(false);
      } else {
        webrtcService.startScreenShare();
        setIsScreenSharing(true);
      }
    } catch (error) {
      toast.error('Screen share failed');
    }
  };

  const handleLeaveCall = async () => {
    await cleanup();
    navigate('/rooms');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 p-4">
        <h1 className="text-xl font-bold">{currentRoom?.title || 'Video Conference'}</h1>
        <p className="text-sm text-gray-400">
          {participants.length + 1} participant{participants.length + 1 !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Video Grid */}
      <div className="flex-1 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full">
          {/* Local Video */}
          <div className="bg-gray-900 rounded-lg overflow-hidden relative">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4 bg-gray-800 px-3 py-1 rounded text-sm">
              You {isVideoEnabled ? '' : '(Video off)'}
            </div>
          </div>

          {/* Remote Videos */}
          {participants.map((participant) => (
            <div
              key={participant.socketId}
              className="bg-gray-900 rounded-lg overflow-hidden relative"
            >
              <video
                ref={(ref) => {
                  if (ref) remoteVideoRefs.current[participant.socketId] = ref;
                }}
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-gray-800 px-3 py-1 rounded text-sm">
                {participant.userName}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="bg-gray-900 border-t border-gray-800 p-4 flex justify-center gap-4">
        <button
          onClick={handleToggleAudio}
          className={`p-3 rounded-full transition ${
            isAudioEnabled
              ? 'bg-gray-700 hover:bg-gray-600'
              : 'bg-red-600 hover:bg-red-700'
          }`}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
        >
          {isAudioEnabled ? <MdMic size={24} /> : <MdMicOff size={24} />}
        </button>

        <button
          onClick={handleToggleVideo}
          className={`p-3 rounded-full transition ${
            isVideoEnabled
              ? 'bg-gray-700 hover:bg-gray-600'
              : 'bg-red-600 hover:bg-red-700'
          }`}
          title={isVideoEnabled ? 'Stop video' : 'Start video'}
        >
          {isVideoEnabled ? <MdVideocam size={24} /> : <MdVideocamOff size={24} />}
        </button>

        <button
          onClick={handleStartScreenShare}
          className={`p-3 rounded-full transition ${
            isScreenSharing
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
        >
          {isScreenSharing ? <MdStopScreenShare size={24} /> : <MdScreenShare size={24} />}
        </button>

        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="p-3 rounded-full bg-gray-700 hover:bg-gray-600 transition"
          title="Chat"
        >
          <MdChat size={24} />
        </button>

        <button
          onClick={handleLeaveCall}
          className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition"
          title="Leave call"
        >
          <MdCallEnd size={24} />
        </button>
      </div>
    </div>
  );
}
